<script setup lang="ts">
import Icon from '~/components/global/Icon.vue'
import { usePaginationState } from '~/composables/pagination/usePaginationState'

const props = defineProps<{
  paginationState: ReturnType<typeof usePaginationState>
}>()

const translationBasePath = `pagination.${props.paginationState.type}`
</script>

<template>
  <div class="c-pagination">
    <div class="c-pagination__count">
      {{
        $t(
          `${translationBasePath}.count`,
          {
            _0: paginationState.state.currentPageCount.value,
            _1: paginationState.state.itemsCount.value,
          },
          paginationState.state.currentPageCount.value
        )
      }}
    </div>

    <div
      v-if="paginationState.state.hasLoadMore.value"
      class="c-pagination__more"
    >
      <IButton
        color="primary"
        class="has-icon"
        :loading="paginationState.state.isMoreLoading.value"
        :show-loading-icon="true"
        @click="paginationState.actions.onLoadMore"
      >
        {{
          $t(
            `${translationBasePath}.loadMore`,
            { _0: paginationState.state.nextPageCount.value },
            paginationState.state.nextPageCount.value
          )
        }}<br />
        <Icon name="chevron-down" />
      </IButton>
    </div>

    <div
      v-if="paginationState.state.visiblePages.value.length > 1"
      class="c-pagination__pages"
    >
      <IButton
        v-if="paginationState.state.hasPrev.value"
        color="primary"
        class="has-icon sc-prev"
        :disabled="
          !!(
            paginationState.state.isNumberLoading.value ||
            paginationState.state.isMoreLoading.value
          )
        "
        @click="paginationState.actions.setPrevPage"
      >
        <Icon name="arrow-left-thick" />
        <span class="_2xl:visible">{{ $t('pagination.previous') }}</span>
      </IButton>

      <template
        v-for="(pagerItem, index) in paginationState.state.visiblePages.value"
      >
        <IButton
          v-if="pagerItem.type === 'page'"
          :key="`n-${pagerItem.number}-${index}`"
          color="primary"
          :class="[
            'sc-page',
            pagerItem.number === paginationState.state.currentPage.value &&
              'is-active',
          ]"
          :loading="
            paginationState.state.isNumberLoading.value === pagerItem.number
          "
          :show-loading-icon="true"
          :disabled="
            !!(
              paginationState.state.isNumberLoading.value ||
              paginationState.state.isMoreLoading.value
            )
          "
          @click="paginationState.actions.setPage(pagerItem.number)"
        >
          {{ pagerItem.number }}
        </IButton>

        <div v-else :key="`e-${pagerItem}-${index}`" class="sc-ellipsis">
          ...
        </div>
      </template>

      <IButton
        v-if="paginationState.state.hasNext.value"
        color="primary"
        class="has-icon sc-next"
        :disabled="
          !!(
            paginationState.state.isNumberLoading.value ||
            paginationState.state.isMoreLoading.value
          )
        "
        @click="paginationState.actions.setNextPage"
      >
        <span class="_2xl:visible">{{ $t('pagination.next') }}</span>
        <Icon name="arrow-right-thick" />
      </IButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-pagination {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 24px;
  user-select: none;

  @include breakpoint-down('lg') {
    display: flex;
    flex-direction: column;
  }

  &__count {
    font-size: 16px;

    @include breakpoint-down('lg') {
      margin-bottom: 12px;
    }
  }

  &__more {
    @include breakpoint-down('lg') {
      margin-bottom: 32px;
    }

    :deep(.button) {
      --button--font-size: 16px;
      --button--font-weight: 600;
      --button--padding-top: 8px;
      --button--padding-bottom: 8px;
      --button--padding-left: 42px;
      --button--padding-right: 42px;
      --button--icon--font-size: 10px;

      min-height: 58px;
    }
  }

  &__pages {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 2px;
    grid-column: 3 / 4;

    .sc-ellipsis {
      aspect-ratio: 1 / 1;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      font-weight: 600;
      height: 100%;
      height: 36px;
    }

    :deep(.button) {
      --button--font-size: 16px;
      --button--padding-top: 4px;
      --button--padding-bottom: 4px;
      --button--padding-left: 4px;
      --button--padding-right: 4px;

      min-height: 36px;

      &.sc-page {
        --button--background: var(--color-green-primary-light-2);
        --button--color: var(--color-dark);
        --button--hover--color: var(--color-light);

        aspect-ratio: 1 / 1;

        &.is-active {
          --button--background: var(--color-light);
          --button--color: var(--color-dark);
          --button--font-weight: 600;

          pointer-events: none;
        }
      }

      &.sc-prev,
      &.sc-next {
        --button--font-weight: 600;
        --button--padding-left: 20px;
        --button--padding-right: 16px;
        --button--icon--font-size: 24px;

        @include breakpoint-down('xl') {
          --button--padding-left: 16px;
          --button--padding-right: 16px;
          --button--icon--font-size: 20px;
        }
      }

      &.sc-prev {
        margin-right: 14px;
      }
      &.sc-next {
        margin-left: 14px;
      }
    }
  }
}
</style>
